import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { EuiButton, EuiFieldNumber, EuiFieldSearch, EuiFormRow, EuiSelect, EuiTextArea } from '@elastic/eui';

const BibleCuvManagement = () => {
  const [versions, setVersions] = useState([]);
  const [books, setBooks] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [filters, setFilters] = useState({ version: 'CUV', bookId: '', chapter: '', search: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadVerses = async (requestedPage = page, requestedFilters = filters) => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await axios.get('/api/admin/bible-cuv', { params: { page: requestedPage, pageSize: 50, version: requestedFilters.version, bookId: requestedFilters.bookId || undefined, chapter: requestedFilters.chapter || undefined, search: requestedFilters.search || undefined } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || requestedPage);
      setSelected(current => {
        if (!current) return null;
        const refreshed = (data.items || []).find(item => item.verseId === current.verseId);
        if (!refreshed) return null;
        setDraftText(refreshed.verseText || '');
        return refreshed;
      });
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.message || '無法載入 MySQL 經文。' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get('/api/admin/bible-cuv/versions').then(({ data }) => setVersions(data || [])).catch(() => setMessage({ type: 'error', text: '無法載入聖經版本清單。' }));
    axios.get('/api/admin/bible-cuv/books').then(({ data }) => setBooks(data || [])).catch(() => setMessage({ type: 'error', text: '無法載入書卷清單。' }));
    loadVerses(1, filters);
  }, []);

  const chooseVerse = verse => { setSelected(verse); setDraftText(verse.verseText || ''); setMessage(null); };
  const search = event => { event.preventDefault(); setSelected(null); loadVerses(1, filters); };
  const save = async () => {
    if (!selected || !draftText.length) { setMessage({ type: 'error', text: '經文內容不能為空。' }); return; }
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await axios.put(`/api/admin/bible-cuv/${encodeURIComponent(selected.verseId)}`, { verseText: draftText, originalVerseText: selected.verseText }, { params: { version: selected.version || filters.version } });
      const updated = { ...selected, verseText: data.verseText };
      setSelected(updated);
      setItems(current => current.map(item => item.verseId === updated.verseId ? updated : item));
      setMessage({ type: 'success', text: `${selected.bookAbbreviation}${selected.chapter}:${selected.startVerse} 已保存到 MySQL。` });
    } catch (error) {
      const currentText = error?.response?.data?.currentVerseText;
      if (currentText !== undefined) { setSelected(current => ({ ...current, verseText: currentText })); setDraftText(currentText); }
      setMessage({ type: 'error', text: error?.response?.data?.message || '保存失敗。' });
    } finally { setSaving(false); }
  };

  return <div className="mysql-bible-admin">
    <div className="admin-section-heading"><div><h2>MySQL 經文管理</h2><p>維護 MySQL 中的中英文聖經版本。書卷、章節和主鍵不可修改。</p></div><strong>{total.toLocaleString()} 節</strong></div>
    {message && <div className={`admin-inline-message is-${message.type}`}>{message.text}</div>}
    <form className="admin-filter-bar" onSubmit={search}>
      <EuiFormRow label="聖經版本"><EuiSelect value={filters.version} options={versions.map(version => ({ value: version.code, text: version.name }))} onChange={event => { const nextFilters = { ...filters, version: event.target.value, bookId: '' }; setSelected(null); setFilters(nextFilters); loadVerses(1, nextFilters); }} /></EuiFormRow>
      <EuiFormRow label="書卷"><EuiSelect value={filters.bookId} options={[{ value: '', text: '全部書卷' }, ...books.map(book => ({ value: book.id, text: `${book.id}. ${['KJV', 'BBE'].includes(filters.version) ? (book.englishName || book.englishCode) : book.name}` }))]} onChange={event => setFilters(current => ({ ...current, bookId: event.target.value }))} /></EuiFormRow>
      <EuiFormRow label="章"><EuiFieldNumber min={1} value={filters.chapter} placeholder="全部" onChange={event => setFilters(current => ({ ...current, chapter: event.target.value }))} /></EuiFormRow>
      <EuiFormRow label="內容或經節"><EuiFieldSearch value={filters.search} placeholder="例如：神說、Rom 14:18" onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} /></EuiFormRow>
      <EuiButton type="submit" fill isLoading={loading}>查詢</EuiButton>
    </form>
    <div className="admin-master-detail">
      <section className="admin-record-list" aria-busy={loading}>
        {items.map(item => <button type="button" key={`${item.version}:${item.verseId}`} className={`admin-record-card ${selected?.version === item.version && selected?.verseId === item.verseId ? 'is-selected' : ''}`} onClick={() => chooseVerse(item)}><strong>{item.bookAbbreviation}{item.chapter}:{item.startVerse}</strong><span>{item.verseText}</span><small>{item.version} · {item.verseId}</small></button>)}
        {!loading && !items.length && <p className="admin-empty-state">找不到符合條件的經文。</p>}
        <div className="admin-pagination-row"><EuiButton size="s" disabled={page <= 1 || loading} onClick={() => loadVerses(page - 1)}>上一頁</EuiButton><span>第 {page} / {totalPages} 頁</span><EuiButton size="s" disabled={page >= totalPages || loading} onClick={() => loadVerses(page + 1)}>下一頁</EuiButton></div>
      </section>
      <section className="admin-editor-panel">
        {!selected ? <p className="admin-empty-state">請從左側選擇一節經文。</p> : <><h3>{selected.bookName} {selected.chapter}:{selected.startVerse}</h3><dl className="admin-metadata-grid"><div><dt>版本</dt><dd>{selected.version}</dd></div><div><dt>Verse ID</dt><dd>{selected.verseId}</dd></div><div><dt>書卷代碼</dt><dd>{selected.bookCode}</dd></div><div><dt>章／節</dt><dd>{selected.chapter}:{selected.startVerse}</dd></div></dl><EuiFormRow label="經文內容" fullWidth><EuiTextArea fullWidth rows={10} value={draftText} onChange={event => setDraftText(event.target.value)} /></EuiFormRow><div className="admin-editor-actions"><EuiButton onClick={() => setDraftText(selected.verseText || '')} disabled={saving || draftText === selected.verseText}>取消修改</EuiButton><EuiButton fill onClick={save} isLoading={saving} disabled={draftText === selected.verseText}>保存到 MySQL</EuiButton></div></>}
      </section>
    </div>
  </div>;
};

export default BibleCuvManagement;
